import React from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { SectionHeader } from "../components/section-header";
import { SectionLoader } from "../components/section-loader";
import { EmptyState } from "@/components/shared/empty-state";
import { PenTool, ArrowRight, Clock } from "lucide-react";

interface FeaturedBlogsSectionProps {
  items: any[];
  isLoading: boolean;
}

export function FeaturedBlogsSection({ items, isLoading }: FeaturedBlogsSectionProps) {
  const displayItems = items.length > 0 ? items.slice(0, 4) : [];

  if (!displayItems.length && !isLoading) return null;

  return (
    <section className="border-b border-border" aria-labelledby="featured-blogs-heading">
      <div className="mx-auto max-w-7xl px-5 py-16 sm:py-20">
        <SectionHeader
          eyebrow="Blogs"
          icon={PenTool}
          title="Featured Blogs"
          href="/blogs"
        />
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-20 bg-neutral-100 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : displayItems.length > 0 ? (
          <div className="space-y-4">
            {displayItems.map((item: any) => (
              <Link key={item.id} href={`/blogs/${item.slug ?? item.id}`} className="group block">
                <Card className="hover:border-neutral-300 transition-all duration-150">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                          <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{item.readTime ?? "6 min read"}</span>
                        </div>
                        <h3 className="text-base font-heading font-semibold leading-snug text-foreground group-hover:text-primary transition-colors line-clamp-1">
                          {item.title}
                        </h3>
                        <p className="text-sm text-muted-foreground leading-relaxed line-clamp-1 mt-1">
                          {typeof item.content === "string" ? item.content.slice(0, 150) : ""}
                        </p>
                      </div>
                      <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0 mt-2 group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <EmptyState icon={PenTool} description="No blogs yet." />
        )}
      </div>
    </section>
  );
}